import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconChart, IconEdit } from '../../../common/icons';
import { useRecipient } from '../../recipients/contexts/recipients';
import RecipientForm from '../../recipients/forms/recipient-form';
import { clientKeys } from '../queries/keys';

import '../../../common/styles/catalog.css';
import '../styles/detail.css';
import '../styles/holdings.css';

// Estado reproductivo de UNA receptora (por fila): la vista `full` trae `current.{available,pregnancy}`.
// Con preñez activa: confirmed/born → Preñada (dx+) · lost → Vacía (dx−) · open → Servida (T.E. sin dx).
// available=true (sin preñez activa) → Vacía. 1 request por receptora (cacheado; lo comparte el tab Sesiones).
function RecipientRepro({ recipientId }) {
    const { data, isLoading, error } = useRecipient(recipientId, 'full');
    if (isLoading) return <span className="cat-table__empty" aria-label="Cargando estado de preñez">…</span>;
    if (error || !data?.current) return <span className="cat-table__empty">—</span>;
    const { available, pregnancy } = data.current;
    if (available === false && pregnancy) {
        const s = pregnancy.state;
        if (s === 'confirmed' || s === 'born') return <span className="hld-repro hld-repro--preg" title={`Preñez ${s}`}>Preñada</span>;
        if (s === 'lost') return <span className="hld-repro hld-repro--empty">Vacía</span>;
        return <span className="hld-repro hld-repro--served" title="Transferida, sin diagnóstico (T.E. sin dx)">Servida</span>;
    }
    return <span className="hld-repro hld-repro--empty">Vacía</span>;
}

// Tab Receptoras del detalle de cliente: receptoras (fincas propias ∪ parcería) con su estado
// reproductivo y edición. Datos del holdings (fetch en el detalle).
export default function RecipientsTab({ recipients = [], clientId, isLoading, error }) {
    const qc = useQueryClient();
    const [editRecipient, setEditRecipient] = useState(null);

    const onSaved = () => {
        setEditRecipient(null);
        qc.invalidateQueries({ queryKey: clientKeys.holdings(clientId) });
    };

    return (
        <section className="client-detail__card">
            <div className="client-detail__section-head">
                <h2 className="client-detail__section-title">Receptoras {!isLoading && <span className="client-detail__count">{recipients.length}</span>}</h2>
            </div>

            {isLoading ? (
                <p className="client-detail__muted">Cargando receptoras…</p>
            ) : error ? (
                <p className="client-detail__muted">No se pudieron cargar las receptoras.</p>
            ) : !recipients.length ? (
                <p className="client-detail__muted">Este cliente no tiene receptoras.</p>
            ) : (
                <table className="cat-table cat-table--compact">
                    <thead>
                        <tr><th>Receptora</th><th>Finca</th><th>Preñez</th><th className="cat-table__actions-col" aria-label="Acciones" /></tr>
                    </thead>
                    <tbody>
                        {recipients.map((r) => (
                            <tr key={r.id} className="cat-table__row">
                                <td>
                                    <Gate domain="recipients" action="read" fallback={<span className="cat-table__name">{r.number}</span>}>
                                        <Link to={`/admin/recipients/${r.id}/stats`} className="cat-table__name">{r.number}</Link>
                                    </Gate>
                                    {r.name && <span className="hld__sub"> · {r.name}</span>}
                                </td>
                                <td>{r.farm?.name || <span className="cat-table__empty">—</span>}</td>
                                <td><RecipientRepro recipientId={r.id} /></td>
                                <td className="cat-table__actions">
                                    <Gate domain="recipients" action="read">
                                        <Link to={`/admin/recipients/${r.id}/stats`} className="cat-table__edit" aria-label="Estadísticas" title="Estadísticas"><IconChart /></Link>
                                    </Gate>
                                    <Gate domain="recipients" action="write">
                                        <button type="button" className="cat-table__edit" onClick={() => setEditRecipient(r)} aria-label="Editar" title="Editar"><IconEdit /></button>
                                    </Gate>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editRecipient && <RecipientForm recipient={editRecipient} onClose={() => setEditRecipient(null)} onSaved={onSaved} />}
        </section>
    );
}
